// Client-side store for the patient-portal session token. The token
// is the only credential we send to the backend (in
// `Authorization: Bearer <token>`); backend resolves it against
// `PortalSession`.
//
// Stored in localStorage. The portal is patient-facing and runs
// primarily inside a Capacitor WebView on the patient's own phone, and
// secondarily as a web app on the patient's own laptop — in both cases
// the device's OS-level lock (biometric / PIN) is the security
// boundary, not the browser tab lifetime. Inside the native shell the
// WebView gets backgrounded and killed for memory routinely, so
// sessionStorage was wiping the token constantly and forcing daily
// re-logins. The backend TTL is 30 days and the server is
// authoritative; `isLikelyValid` short-circuits clearly-expired
// tokens client-side. A future pass can move this to httpOnly cookies
// (web) and Capacitor Preferences / Keychain (native).

const TOKEN_KEY = 'healthcom-schedule.portalSession.token';
const PATIENT_KEY = 'healthcom-schedule.portalSession.patient';
const EXPIRES_KEY = 'healthcom-schedule.portalSession.expiresAt';

export type StoredPatientContext = {
  patientId: string;
  clinicId: number;
  fname?: string | null;
  lname?: string | null;
};

export const portalSession = {
  getToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },

  setToken(token: string, expiresAt?: string) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      if (expiresAt) localStorage.setItem(EXPIRES_KEY, expiresAt);
    } catch {
      /* storage blocked — sign-in won't persist across reloads */
    }
  },

  getExpiresAt(): string | null {
    try {
      return localStorage.getItem(EXPIRES_KEY);
    } catch {
      return null;
    }
  },

  getPatient(): StoredPatientContext | null {
    try {
      const raw = localStorage.getItem(PATIENT_KEY);

      return raw ? (JSON.parse(raw) as StoredPatientContext) : null;
    } catch {
      return null;
    }
  },

  setPatient(patient: StoredPatientContext) {
    try {
      localStorage.setItem(PATIENT_KEY, JSON.stringify(patient));
    } catch {
      /* ignore */
    }
  },

  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PATIENT_KEY);
      localStorage.removeItem(EXPIRES_KEY);
    } catch {
      /* ignore */
    }
  },

  // Best-effort liveness check. The wrapper still validates server-
  // side; this is just to avoid sending a clearly-expired token from
  // the client when we already know better.
  isLikelyValid(): boolean {
    const token = this.getToken();

    if (!token) return false;

    const expiresAt = this.getExpiresAt();

    if (!expiresAt) return true;

    const expiresAtMs = Date.parse(expiresAt);

    return Number.isFinite(expiresAtMs) && expiresAtMs > Date.now();
  },
};
